import {
  AlertTriangle,
  BarChart3,
  CalendarClock,
  CircleGauge,
  FileChartColumn,
  FlaskConical,
  LayoutDashboard,
  LineChart,
  PieChart,
  ShieldCheck,
  Table2,
  TrendingUp,
} from "lucide-react";
import { dashboardTemplates } from "../../data";
import type { PageId } from "../../types";

interface StaticWireframePageProps {
  page: Exclude<
    PageId,
    | "analyst"
    | "workspace"
    | "my-dashboards"
    | "my-reports"
    | "my-artifacts"
    | "dashboard-canvas"
    | "report-canvas"
    | "artifact-canvas"
    | "data-sources"
    | "metrics"
  >;
}

function DashboardsView() {
  return (
    <div className="content-stack">
      <section className="hero dashboard-hero">
        <div>
          <span className="section-kicker">Dashboard design</span>
          <h1>Dashboards</h1>
          <p>Select a governed DMI template or start a new dashboard canvas.</p>
        </div>
        <button type="button" className="button light">Create new dashboard</button>
      </section>
      <section className="panel">
        <div className="panel-heading">
          <div><h2>Select a template</h2><p>DMI starter layouts backed by certified metrics</p></div>
        </div>
        <div className="template-grid">
          {dashboardTemplates.map((template, index) => (
            <article key={template.name}>
              <div className="template-preview">
                {index % 2 === 0 ? <BarChart3 size={34} /> : <LineChart size={34} />}
              </div>
              <div><strong>{template.name}</strong><p>{template.detail}</p><small>{template.charts}</small></div>
              <button type="button" className="button secondary">Use template</button>
            </article>
          ))}
        </div>
      </section>
      <div className="two-column-layout">
        <section className="panel">
          <div className="panel-heading"><div><h2>Create new</h2><p>Compose from certified metrics</p></div></div>
          <div className="palette-list">
            <span><CircleGauge size={18} /> KPI number</span>
            <span><LineChart size={18} /> Time series</span>
            <span><BarChart3 size={18} /> Bar chart</span>
            <span><PieChart size={18} /> Donut chart</span>
            <span><Table2 size={18} /> Detail table</span>
          </div>
        </section>
        <section className="panel">
          <div className="panel-heading"><div><h2>Dashboard canvas</h2><p>Arrange and resize chart blocks</p></div></div>
          <div className="static-canvas">
            <div>KPI â€” Certified metric</div><div>Trend â€” Governed time dimension</div>
            <div className="wide">Drop another chart here</div>
          </div>
        </section>
      </div>
    </div>
  );
}

function ExploreView() {
  return (
    <div className="content-stack">
      <section className="panel breadcrumb-panel">
        <span>All services</span><b>â€º</b><span>SVC_ehr</span><b>â€º</b><span>P1</span><b>â€º</b><span>Network</span>
        <button type="button" className="button secondary">Reset</button>
      </section>
      <div className="two-column-layout">
        <section className="panel">
          <div className="panel-heading"><div><h2>Explore canvas</h2><p>Same BCL model used by AI Analyst</p></div></div>
          <div className="horizontal-chart">
            {[92, 68, 52, 43, 34].map((width, index) => (
              <div key={width}><span>{["Night Â· Network", "Night Â· Database", "Swing Â· Network", "Day Â· Network", "Day Â· App"][index]}</span><i style={{ width: `${width}%` }} /></div>
            ))}
          </div>
        </section>
        <section className="panel">
          <div className="panel-heading"><div><h2>Explain this change</h2><p>Contribution decomposition</p></div></div>
          <div className="insight-callout"><TrendingUp size={22} /><p>MTTR moved from <strong>6.2h to 8.7h</strong>. Network night shift explains most of the delta.</p></div>
        </section>
      </div>
      <section className="panel">
        <div className="panel-heading"><div><h2>Drill to detail</h2><p>Row-level, policy-filtered</p></div></div>
        <table className="data-table"><thead><tr><th>Incident</th><th>Service</th><th>Group</th><th className="num">Hours</th><th>SLA</th></tr></thead>
          <tbody><tr><td>INC0042311</td><td>SVC_ehr</td><td>Network</td><td className="num">16.8</td><td className="danger-text">Breach</td></tr>
            <tr><td>INC0042190</td><td>SVC_ehr</td><td>Database</td><td className="num">8.3</td><td className="success-color">Met</td></tr></tbody>
        </table>
      </section>
    </div>
  );
}

function ReportsView({ builder = false }: { builder?: boolean }) {
  if (builder) {
    return (
      <div className="two-column-layout">
        <section className="panel report-document">
          <div className="panel-heading"><div><h2>Monthly Service Performance</h2><p>July 2026 Â· Prepared for the COR</p></div></div>
          <h3>Executive summary</h3>
          <p>Median P1 resolution time rose 41% this period. All figures resolve to governed metric definitions and an audit identifier.</p>
          <div className="static-canvas"><div>MTTR trend</div><div>SLA by service</div></div>
          <h3>Actions and owners</h3>
          <table className="data-table"><tbody><tr><td>Close problem record</td><td>Network Ops</td><td>In progress</td></tr></tbody></table>
        </section>
        <section className="panel"><div className="panel-heading"><div><h2>Delivery & AI settings</h2><p>Schedule and distribution</p></div></div>
          <div className="settings-list"><span><CalendarClock /> Monthly Â· first day</span><span><FileChartColumn /> PDF and XLSX</span><span><ShieldCheck /> Human approval</span></div>
        </section>
      </div>
    );
  }
  return (
    <section className="panel">
      <div className="panel-heading"><div><h2>Report Library</h2><p>Scheduled deliverables, scorecards, and alerts</p></div><button type="button" className="button primary">New report</button></div>
      <table className="data-table"><thead><tr><th>Name</th><th>Type</th><th>Cadence</th><th>Status</th><th>Next</th></tr></thead>
        <tbody>
          <tr><td>Monthly Service Performance</td><td>Report</td><td>Monthly</td><td className="success-color">Delivered</td><td>Aug 01</td></tr>
          <tr><td>Weekly Program Health</td><td>Scorecard</td><td>Weekly</td><td className="success-color">Delivered</td><td>Aug 03</td></tr>
          <tr><td>P1 MTTR breach</td><td>Alert</td><td>15 minutes</td><td className="danger-text">Fired Ã—3</td><td>â€”</td></tr>
        </tbody>
      </table>
    </section>
  );
}

function AdvancedView({ whatIf = false }: { whatIf?: boolean }) {
  if (whatIf) {
    return (
      <div className="content-stack">
        <section className="panel scenario-title"><strong>Scenario: Close problem record + add night-shift capacity</strong><span className="status-badge certified">Baseline FY26 Q3</span></section>
        <div className="two-column-layout">
          <section className="panel"><div className="panel-heading"><div><h2>Projected outcome</h2><p>Baseline, scenario, and target</p></div></div><div className="line-placeholder"><FlaskConical size={38} /><span>90-day scenario projection</span></div></section>
          <section className="panel"><div className="panel-heading"><div><h2>Assumption drivers</h2><p>Versioned scenario inputs</p></div></div>
            <div className="slider-list">{["Night-shift headcount", "Problem close date", "Automation coverage", "Vendor SLA"].map((item, i) => <label key={item}>{item}<input type="range" defaultValue={40 + i * 10} /></label>)}</div>
          </section>
        </div>
      </div>
    );
  }
  return (
    <div className="content-stack">
      <section className="panel chip-toolbar"><span className="active">Forecast</span><span>Anomaly detection</span><span>Root cause</span><span>Cohort</span><span>Correlation</span></section>
      <div className="three-column-layout">
        <section className="panel"><h2>90-day forecast</h2><div className="line-placeholder"><LineChart size={35} /><span>Point forecast with confidence interval</span></div></section>
        <section className="panel"><h2>Active anomalies</h2><div className="alert-list"><span><AlertTriangle /> MTTR Â· Network <b>+3.1Ïƒ</b></span><span><AlertTriangle /> Backlog aging <b>+2.8Ïƒ</b></span></div></section>
        <section className="panel"><h2>Model transparency</h2><div className="settings-list"><span>MTTR forecast <b>MAPE 6.4%</b></span><span>Anomaly STL <b>FPR 3.1%</b></span></div></section>
      </div>
    </div>
  );
}

function TrustView() {
  return (
    <div className="content-stack">
      <div className="kpi-row">
        <div><span>Questions (30d)</span><strong>4,182</strong></div>
        <div><span>Grounded in BCL</span><strong>97.4%</strong></div>
        <div><span>User flags</span><strong>31</strong></div>
        <div><span>Policy denials</span><strong>216</strong></div>
      </div>
      <div className="two-column-layout">
        <section className="panel"><div className="panel-heading"><div><h2>Audit log</h2><p>Who asked what and which policy applied</p></div></div>
          <table className="data-table"><tbody><tr><td>09:14</td><td>v.makwana</td><td>P1 spike</td><td>q-8f31c2</td></tr><tr><td>08:12</td><td>m.oduya</td><td>Salary by employee</td><td className="danger-text">Denied</td></tr></tbody></table>
        </section>
        <section className="panel"><div className="panel-heading"><div><h2>Access & policy</h2><p>Roles, row filters, and member scope</p></div></div>
          <div className="settings-list"><span><ShieldCheck /> Service Delivery Â· regional filter</span><span><ShieldCheck /> Program Manager Â· owned programs</span><span><ShieldCheck /> Executive Â· summary members</span></div>
        </section>
      </div>
    </div>
  );
}

export function StaticWireframePage({ page }: StaticWireframePageProps) {
  if (page === "dashboards") return <DashboardsView />;
  if (page === "explore") return <ExploreView />;
  if (page === "reports") return <ReportsView />;
  if (page === "report-builder") return <ReportsView builder />;
  if (page === "advanced") return <AdvancedView />;
  if (page === "what-if") return <AdvancedView whatIf />;
  if (page === "trust") return <TrustView />;
  return (
    <div className="empty-state">
      <LayoutDashboard size={30} />
      <strong>Wireframe view</strong>
    </div>
  );
}

